import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { chatAPI } from 'services/api';
import { RootState } from 'store/rootReducer';
import { ContatoMencionado } from 'types';

import { AtendimentoInterface } from './atendimentos';
import { CanalInterface } from './canais';
import { ContatoInterface } from './contatos';
import { WppTemplateData } from './templates';
import { UsuarioInterface } from './usuarios';

export enum TiposMensagemEnum {
  AUDIO = 'AUDIO',
  CONTATO = 'CONTATO',
  DOCUMENTO = 'DOCUMENTO',
  EVENTO = 'EVENTO',
  IMAGEM = 'IMAGEM',
  LOCALIZACAO = 'LOCALIZACAO',
  TEXTO = 'TEXTO',
  VIDEO = 'VIDEO',
  TEMPLATE_WHATSAPP = 'TEMPLATE_WHATSAPP',
  WPP_CTA = 'WPP_CTA',
  WPP_REPLY_BTN = 'WPP_REPLY_BTN',
  WPP_REPLY_BTN_RESP = 'WPP_REPLY_BTN_RESP',
  WPP_LIST = 'WPP_LIST',
  WPP_LIST_RESP = 'WPP_LIST_RESP',
  ARRAY_CONTATOS = 'ARRAY_CONTATOS',
  RESUMO_IA = 'RESUMO_IA',
  COMENTARIO = 'COMENTARIO',
  // MIDIA = 'MIDIA',
}

export type AutorMensagemTypes =
  | 'ADMINISTRADOR'
  | 'ATENDENTE'
  | 'ATENDENTE_EXTERNO'
  | 'CONTATO'
  | 'BOT'
  | 'AUTO';

export type StatusMensagemType = 'ENVIADO' | 'RECEBIDO' | 'LIDO' | 'ERRO';

export enum TiposEventoMensagemEnum {
  CHAMADA_VOZ_PERDIDA = 'CHAMADA_VOZ_PERDIDA',
  CHAMADA_VIDEO_PERDIDA = 'CHAMADA_VIDEO_PERDIDA',
  TRANS_ATENDIMENTO = 'TRANS_ATENDIMENTO',
  TRANS_ATENDIMENTO_CANCELADO = 'TRANS_ATENDIMENTO_CANCELADO',
  TRANS_ATENDIMENTO_RECUSADO = 'TRANS_ATENDIMENTO_RECUSADO',
  TRANS_ATENDIMENTO_ACEITA = 'TRANS_ATENDIMENTO_ACEITA',
  ATENDIMENTO_FINALIZADO = 'ATENDIMENTO_FINALIZADO',
  ATENDIMENTO_INICIADO = 'ATENDIMENTO_INICIADO',
  INATIVIDADE_ATENDENTE = 'INATIVIDADE_ATENDENTE',
  INATIVIDADE_CONTATO = 'INATIVIDADE_CONTATO',
  MUDANCA_POSICAO_ATENDIMENTO = 'MUDANCA_POSICAO_ATENDIMENTO',
  INDISPONIBILIDADE_EQUIPE = 'INDISPONIBILIDADE_EQUIPE',
  CONTATO_REMOVIDO = 'CONTATO_REMOVIDO',
  CONTATO_ADICIONADO = 'CONTATO_ADICIONADO',
  DATA_MENSAGENS = 'DATA_MENSAGENS',
  ATENDIMENTO_FIXADO = 'ATENDIMENTO_FIXADO',
  ATENDIMENTO_DESAFIXADO = 'ATENDIMENTO_DESAFIXADO',
}

export enum TiposDeMensagemInterativaEnum {
  CTA_URL = 'cta_url',
  BUTTON = 'button',
  LIST = 'list',
  REPLY = 'reply',
  LIST_REPLY = 'list_reply',
  BUTTON_REPLY = 'button_reply',
}

export enum TiposDeCabecalhoMensagemInterativaEnum {
  TEXTO = 'text',
  IMAGEM = 'image',
  VIDEO = 'video',
  DOCUMENTO = 'document',
}

export type CabecalhoDeMensagemInterativa = {
  type?: TiposDeCabecalhoMensagemInterativaEnum;
  text?: string;
  image?: {
    link: string;
  };
  video?: {
    link: string;
  };
  document?: {
    link: string;
  };
};

export type AcaoDeCta = {
  name: TiposDeMensagemInterativaEnum;
  parameters: {
    display_text: string;
    url: string;
  };
};

export type BotaoDeRespostaInterativa = {
  type: TiposDeMensagemInterativaEnum.REPLY;
  reply: {
    id: string;
    title: string;
  };
};

export type AcaoDeRespostaInterativa = {
  buttons: BotaoDeRespostaInterativa[];
};

export type RowListaInterativa = {
  id: string;
  title: string;
  description: string;
};

export type SecaoListaInterativa = {
  title: string;
  rows: RowListaInterativa[];
};

export type AcaoDeListaInterativa = {
  button: string;
  sections: SecaoListaInterativa[];
};

export interface MensagemCtaInterface {
  type: TiposDeMensagemInterativaEnum;
  action: AcaoDeCta;
  header?: CabecalhoDeMensagemInterativa;
  body: {
    text: string;
  };
  footer?: {
    text: string;
  };
}

export interface MensagemRespostaInterativaInterface {
  type: TiposDeMensagemInterativaEnum.BUTTON;
  header?: CabecalhoDeMensagemInterativa;
  action: AcaoDeRespostaInterativa;
  body: {
    text: string;
  };
  footer?: {
    text: string;
  };
}

export interface MensagemListaInterativaInterface {
  type: TiposDeMensagemInterativaEnum.LIST;
  action: AcaoDeListaInterativa;
  header?: {
    text: string;
  };
  body: {
    text: string;
  };
  footer?: {
    text: string;
  };
}

export declare interface ContarNovaMensagemInterface {
  atendimentoId: string;
  totalNaoLidas: number;
  posicao: 'EQUIPE' | 'ATENDENTE';
}

export declare interface ContarNovaMensagemGrupoInterface {
  grupoChatId: string;
  totalNaoLidas: number;
  ultimaMensagemEm: string;
}

export declare interface EventoMensagemInterface {
  tipo: TiposEventoMensagemEnum;
}

export interface LocalizacaoMensagemType {
  latitude: number;
  longitude: number;
  urlImagem: string;
}

export declare interface BaixandoMidiaMensagemInterface {
  atendimentoId: string;
  mensagemId: string;
}

export declare interface AtualizarMidiaMensagemInterface {
  atendimentoId: string;
  mensagemId: string;
  midiaDownloadErro: boolean;
  midiaUrl: string;
  mensagemTipo?: TiposMensagemEnum;
  midiaDuracao?: number | null;
}

export declare interface AtualizarReacoesMensagemInterface {
  action: 'remove' | 'add';
  atendimentoId: string;
  mensagem: MensagemInterface;
  author: 'contato' | 'atendente';
  reactions: {
    lastReactions: string[] | null;
    msgReagidaId: string;
    total: number;
  };
}

export declare interface AtualizarReacoesMensagemGrupoInterface {
  grupoChatId: string;
  reactions: {
    lastReactions: string[] | null;
    msgReagidaId: string;
    total: number;
  };
}

export interface ReplyMensagemInterativaInterface {
  type: TiposDeMensagemInterativaEnum.LIST_REPLY;
  list_reply?: {
    id: string;
    title: string;
  };
  button_reply?: {
    id: string;
    title: string;
  };
}

export type AudioTranscriptionStatus = 'completed' | 'failed' | 'requested';

export type AudioTranscriptionMetadata = {
  status: AudioTranscriptionStatus;
  statusAt: string;
  errorCode: string | null;
};

export declare interface MensagemInterface {
  id: string;
  grupoChatId: string;
  atendimentoId: string;
  ackStatus: StatusMensagemType;
  ackMotivoErro: string | null;
  ackDescricaoErro: string | null;
  autor: AutorMensagemTypes;
  criadoEm: string;
  tipo: TiposMensagemEnum;
  isEncaminhada: boolean;
  mensagem: string;
  ultimaMensagemEm: string;
  mensagemApagadaEm: string;
  mensagemCitada: MensagemInterface;
  subType?: string;
  mensagemContato?: ContatoInterface | ContatoInterface[];
  contato?: ContatoInterface;
  contatoId?: string;
  mensagemLocalizacao: LocalizacaoMensagemType;
  midiaDownloadErro: boolean;
  midiaTamanho: number;
  midiaTitulo: string;
  midiaFileName: string;
  ordem: number;
  midiaDuracao: number | null;
  midiaUrl: string;
  midiaUrlPrevia: string;
  canal?: CanalInterface;
  evento: EventoMensagemInterface | undefined;
  usuario: UsuarioInterface | undefined;
  baixando?: boolean; // OBS: Este atributo não vem na resposta da api
  removidoPorAdminEm?: string | Date;
  reacoesEmojis?: string[] | null;
  reacoesTotal: number;
  isEditada: boolean;
  naoIniciaAtendimento?: boolean;
  whatsappTemplateData: WppTemplateData | null;
  whatsappInteractiveComponents:
    | MensagemCtaInterface
    | MensagemRespostaInterativaInterface
    | MensagemListaInterativaInterface
    | ReplyMensagemInterativaInterface
    | null;
  messageMetadata: {
    audioTranscription: AudioTranscriptionMetadata;
  } | null;
  contatosMencionados?: {
    contatoId: string | null;
    waPhoneNumberId: string;
    contatoNome?: string;
  }[];
}

export declare interface RespostaProntaInterface {
  id: string;
  nome: string;
  categoriaId: string;
  categoria: {
    id: string;
    nome: string;
  };
  resposta: {
    texto: string;
  };
}

export declare interface PageInfo {
  isLastPage?: boolean;
  limitPerPage?: number;
  nextPage?: number | null;
  page?: number;
  previousPage?: number | null;
}

export declare interface ParamsPage {
  page?: number;
  limit?: number;
}

export declare interface ReacaoInterface {
  id: string;
  reacao: string;
  registradoEm: string;
  contato: ContatoInterface;
  atendente: {
    id: string;
    nome: string;
    foto: string;
  };
}

export declare interface AtualizarMensagemEditadaAtendimentoInterface {
  atendimentoId: string;
  mensagem: MensagemInterface;
}

export declare interface AtualizarMensagemEditadaGrupoInterface {
  grupoChatId: string;
  mensagem: MensagemInterface;
}

export declare interface HistoricoMensagemEditadaInterface {
  id: string;
  mensagem: string;
  registradoEm: string;
}

export declare interface PreviewMensagemInterface {
  id: string;
  tipo: TiposMensagemEnum;
  order: number;
  atendimentoId: string;
  registeredIn: string;
  preview: string;
  autor: AutorMensagemTypes;
}

export declare interface PreviewMensagemGrupoInterface
  extends Omit<PreviewMensagemInterface, 'atendimentoId'> {
  groupId: string;
}

type MensagensState = {
  replyMessage: MensagemInterface | null;
  mensagensContato: MensagemInterface[];
  mensagensContatoCursor: CursorInterface;
  loadingMensagensContato: boolean;

  mensagensAtendimento: MensagemInterface[];
  mensagensAtendimentoCursor: CursorInterface;
  loadingMensagensAtendimento: boolean;

  mensagensAtendimentoNaFila: MensagemInterface[];
  mensagensAtendimentoNaFilaCursor: CursorInterface;
  loadingMensagensAtendimentoNaFila: boolean;

  mensagensGrupo: MensagemInterface[];
  mensagensGrupoCursor: CursorInterface;
  loadingMensagensGrupo: boolean;

  mensagensFinalizadas: MensagemInterface[];
  mensagensFinalizadasCursor: CursorInterface;
  loadingMensagensFinalizadas: boolean;

  respostasProntas: RespostaProntaInterface[];
  loadingRespostasProntas: boolean;

  mensagemCitada?: MensagemInterface;
  loadingMensagemCitada: boolean;

  midiasAtendimento: MensagemInterface[];
  documentosAtendimento: MensagemInterface[];
  midiasGrupo: MensagemInterface[];
  documentosGrupo: MensagemInterface[];

  midiasAtendimentoInfoPage: PageInfo;
  documentosAtendimentoInfoPage: PageInfo;
  midiasGrupoInfoPage: PageInfo;
  documentosGrupoInfoPage: PageInfo;

  loadingMidiasAtendimento: boolean;
  loadingDocumentosAtendimento: boolean;
  loadingMidiasGrupo: boolean;
  loadingDocumentosGrupo: boolean;
  loadingEnviarArquivo: boolean;

  previewMensagens: PreviewMensagemInterface[];
  mensagemIdPesquisada: string | null;
  searchQuery: string;
  mensagemEstaMontada: boolean;
  loadingPreviewMensagens: boolean;

  atendimentoCursorAConsumir: CursorInterface[];
  grupoCursorAConsumir: CursorInterface[];
  atendimentoFilaCursorAConsumir: CursorInterface[];
  historicoCursorAConsumir: CursorInterface[];

  reacoesMensagem: ReacaoInterface[];
  loadingReacoesMensagem: boolean;

  historicoMensagemEditada: HistoricoMensagemEditadaInterface[];
  loadingHistoricoMensagemEditada: boolean;

  mensagensParaEncaminhar: string[];
  totalMensagensParaEncaminhar: number;
  loadingEncaminharMensagens: boolean;
  isEncaminhandoMensagens: boolean;

  loadingReagirMensagem: boolean;
  loadingRemoverReacao: boolean;

  botoesInterativosDesabilitados: {
    [key: string]: boolean;
  };

  isGravandoAudio: boolean;
};

export const fetchMensagensByContato = createAsyncThunk(
  'mensagens/fetchMensagensByContato',
  async (params: { contatoId: string; cursor?: CursorInterface }) => {
    const { contatoId, cursor } = params;
    const response = await chatAPI.get(`/mensagens/contato/${contatoId}`, {
      params: { afterCursor: cursor?.afterCursor },
    });
    return {
      data: response.data.data as MensagemInterface[],
      cursor: response.data.cursor as CursorInterface,
    };
  },
);

export const fetchMensagensByAtendimento = createAsyncThunk(
  'mensagens/fetchMensagensByAtendimento',
  async (params: { atendimentoId: string; cursor?: CursorInterface }) => {
    const { atendimentoId, cursor } = params;

    const response = await chatAPI.get(
      `/mensagens/atendimento/${atendimentoId}`,
      {
        params: {
          afterCursor: cursor?.afterCursor,
          beforeCursor: cursor?.beforeCursor,
        },
      },
    );
    return {
      data: response.data.data as MensagemInterface[],
      cursor: response.data.cursor as CursorInterface,
    };
  },
);

export const fetchMensagensByGrupo = createAsyncThunk(
  'mensagens/fetchMensagensByGrupo',
  async (params: { grupoChatId: string; cursor?: CursorInterface }) => {
    const { grupoChatId, cursor } = params;
    const response = await chatAPI.get(`/mensagens/grupo-chat/${grupoChatId}`, {
      params: {
        afterCursor: cursor?.afterCursor,
        beforeCursor: cursor?.beforeCursor,
      },
    });
    return {
      data: response.data.data as MensagemInterface[],
      cursor: response.data.cursor as CursorInterface,
    };
  },
);

export const fetchMensagensFinalizadasByAtendimento = createAsyncThunk(
  'mensagens/fetchMensagensFinalizadasByAtendimento',
  async (params: {
    atendimentoId: string;
    cursor?: CursorInterface;
    limit?: number;
    apenasMsgsDesteAtendimento?: boolean;
  }) => {
    const {
      atendimentoId,
      cursor,
      limit,
      apenasMsgsDesteAtendimento = true,
    } = params;

    const response = await chatAPI.get(
      `/mensagens/atendimento/${atendimentoId}`,
      { params: { ...cursor, limit, apenasMsgsDesteAtendimento } },
    );
    return {
      data: response.data.data as MensagemInterface[],
      cursor: response.data.cursor as CursorInterface,
    };
  },
);

export const fetchMensagensFinalizadasAtendimentoFila = createAsyncThunk(
  'mensagens/fetchMensagensFinalizadasAtendimentoFila',
  async (params: {
    atendimentoId: string;
    cursor?: CursorInterface;
    limit?: number;
  }) => {
    const { atendimentoId, cursor, limit } = params;

    const response = await chatAPI.get(
      `/mensagens/atendimento/${atendimentoId}`,
      { params: { ...cursor, limit, apenasMsgsDesteAtendimento: false } },
    );
    return {
      data: response.data.data as MensagemInterface[],
      cursor: response.data.cursor as CursorInterface,
    };
  },
);

export const fetchRespostasProntas = createAsyncThunk(
  'mensagens/fetchRespostasProntas',
  async (filter?: string) => {
    const response = await chatAPI.get('mensagens/respostas-prontas', {
      params: { filter },
    });
    return response.data.data as RespostaProntaInterface[];
  },
);

export const fetchMensagemCitada = createAsyncThunk(
  'mensagens/fetchMensagemCitada',
  async (mensagemId?: string) => {
    const response = await chatAPI.get(
      `mensagens/${mensagemId}/mensagem-citada`,
    );
    return response.data.data as MensagemInterface;
  },
);

export const fetchMidiasAtendimento = createAsyncThunk(
  'mensagens/fetchMidiasAtendimento',
  async (data: { atendimentoId: string; params?: ParamsPage }) => {
    const { atendimentoId, params } = data;

    const response = await chatAPI.get(
      `mensagens/atendimento/${atendimentoId}/midias`,
      { params },
    );
    return {
      data: response.data.data as MensagemInterface[],
      pageInfo: response.data.pageInfo as PageInfo,
    };
  },
);

export const fetchDocumentosAtendimento = createAsyncThunk(
  'mensagens/fetchDocumentosAtendimento',
  async (data: { atendimentoId: string; params?: ParamsPage }) => {
    const { atendimentoId, params } = data;
    const response = await chatAPI.get(
      `mensagens/atendimento/${atendimentoId}/documentos`,
      { params },
    );
    return {
      data: response.data.data as MensagemInterface[],
      pageInfo: response.data.pageInfo as PageInfo,
    };
  },
);

export const fetchMidiasGrupo = createAsyncThunk(
  'mensagens/fetchMidiasGrupo',
  async (data: { grupoChatId: string; params?: ParamsPage }) => {
    const { grupoChatId, params } = data;
    const response = await chatAPI.get(
      `mensagens/grupo-chat/${grupoChatId}/midias`,
      { params },
    );
    return {
      data: response.data.data as MensagemInterface[],
      pageInfo: response.data.pageInfo as PageInfo,
    };
  },
);

export const reagirMensagem = createAsyncThunk(
  'mensagens/reagirMensagem',
  async (data: { mensagemId: string; reaction: string }) => {
    const { mensagemId, reaction } = data;

    await chatAPI.put(`/v2/messages/${mensagemId}/react`, { reaction });
  },
);

export const removerReacao = createAsyncThunk(
  'mensagens/removerReacao',
  async (data: { mensagemId: string }) => {
    const { mensagemId } = data;
    await chatAPI.delete(`/v2/messages/${mensagemId}/react`);
  },
);

export const fetchReacoesMensagem = createAsyncThunk(
  'mensagens/fetchReacoesMensagem',
  async (data: { mensagemId: string }) => {
    const { mensagemId } = data;
    const response = await chatAPI.get(`mensagens/${mensagemId}/reacoes`);
    return response.data.data as ReacaoInterface[];
  },
);

export const fetchHistoricoMensagemEditada = createAsyncThunk(
  'mensagens/fetchHistoricoMensagemEditada',
  async (data: { mensagemId: string }) => {
    const { mensagemId } = data;
    const response = await chatAPI.get(`mensagens/${mensagemId}/edicoes`);
    return response.data.data as HistoricoMensagemEditadaInterface[];
  },
);

export const fetchDocumentosGrupo = createAsyncThunk(
  'mensagens/fetchDocumentosGrupo',
  async (data: { grupoChatId: string; params?: ParamsPage }) => {
    const { grupoChatId, params } = data;
    const response = await chatAPI.get(
      `mensagens/grupo-chat/${grupoChatId}/documentos`,
      { params },
    );

    return {
      data: response.data.data as MensagemInterface[],
      pageInfo: response.data.pageInfo as PageInfo,
    };
  },
);

export const fetchPreviewMensagens = createAsyncThunk(
  'mensagens/fetchPreviewMensagens',
  async (params: {
    search: string;
    contactId?: string;
    channelCode?: string;
  }) => {
    const response = await chatAPI.get(`/v2/messages/preview`, {
      params: { ...params },
    });

    return response.data as PreviewMensagemInterface[];
  },
);

export const fetchPreviewMensagensGrupo = createAsyncThunk(
  'mensagens/fetchPreviewMensagensGrupo',
  async (params: { search: string; groupId: string }) => {
    const response = await chatAPI.get(`/v2/messages-groups/preview`, {
      params,
    });

    return response.data as PreviewMensagemGrupoInterface[];
  },
);

export const navegarParaMensagem = createAsyncThunk(
  'mensagens/navegarParaMensagem',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.get(`/v2/messages/${id}/cursor`);

      return {
        data: response.data as {
          afterCursor: string;
        },
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const navegarParaMensagemGrupo = createAsyncThunk(
  'mensagens/navegarParaMensagemGrupo',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.get(`/v2/messages-groups/${id}/cursor`);

      return {
        data: response.data as {
          afterCursor: string;
        },
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const enviarArquivo = createAsyncThunk(
  'mensagens/enviarArquivo',
  async (
    data: {
      arquivo: Blob;
      atendimentoId: string;
      isGravacaoAudio?: boolean;
      duracao?: number;
      mensagemCitadaId?: string;
      midiaTitulo?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const {
        arquivo,
        atendimentoId,
        isGravacaoAudio,
        duracao,
        mensagemCitadaId,
        midiaTitulo,
      } = data;
      const formData = new FormData();

      formData.append('arquivo', arquivo);
      formData.append('atendimentoId', atendimentoId);

      if (midiaTitulo) {
        formData.append('midiaTitulo', midiaTitulo as never);
      }

      // se for a gravação de um audio
      if (isGravacaoAudio) {
        const fileName = `Tarelo ${new Date().toISOString()}.ogg`;

        formData.delete('arquivo');
        formData.append('arquivo', arquivo, fileName);
        formData.append('isGravacaoAudio', isGravacaoAudio as never);
        formData.append('duracao', duracao as never);
      }
      // se for a resposta de uma mensagem
      if (mensagemCitadaId) {
        formData.append('mensagemCitadaId', mensagemCitadaId);
      }

      const response = await chatAPI.post(
        '/mensagens/enviar-arquivo',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data.data as MensagemInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const enviarArquivoGrupo = createAsyncThunk(
  'mensagens/enviarArquivoGrupo',
  async (
    data: {
      arquivo: Blob;
      grupoChatId: string;
      isGravacaoAudio?: boolean;
      duracao?: number;
      mensagemCitadaId?: string;
      midiaTitulo?: string;
      canalId?: string;
      contatosMencionados?: ContatoMencionado[];
    },
    { rejectWithValue },
  ) => {
    try {
      const {
        arquivo,
        grupoChatId,
        isGravacaoAudio,
        duracao,
        mensagemCitadaId,
        midiaTitulo,
        canalId,
        contatosMencionados,
      } = data;
      const formData = new FormData();
      formData.append('arquivo', arquivo);
      formData.append('grupoChatId', grupoChatId);

      if (midiaTitulo) {
        formData.append('midiaTitulo', midiaTitulo as never);
      }

      if (canalId) {
        formData.append('canalId', canalId as never);
      }

      if (contatosMencionados && contatosMencionados.length > 0) {
        formData.append(
          'contatosMencionados',
          JSON.stringify(contatosMencionados),
        );
      }

      // se for a gravação de um audio
      if (isGravacaoAudio) {
        const fileName = `Tarelo ${new Date().toISOString()}.ogg`;

        formData.delete('arquivo');
        formData.append('arquivo', arquivo, fileName);
        formData.append('isGravacaoAudio', isGravacaoAudio as never);
        formData.append('duracao', duracao as never);
      }
      // se for a resposta de uma mensagem
      if (mensagemCitadaId) {
        formData.append('mensagemCitadaId', mensagemCitadaId);
      }

      const response = await chatAPI.post(
        '/mensagens/enviar-arquivo-grupo',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data.data as MensagemInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// encaminhar mensagens
export const encaminharMensagens = createAsyncThunk(
  'mensagens/encaminhar',
  async (
    data: {
      contactIds: string[];
      channelId: string;
      teamId: string;
      messageIds: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await chatAPI.post(`v2/messages/forward`, data);

      return response.data as {
        atendimento: AtendimentoInterface;
        messages: MensagemInterface[];
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const transcreverAudio = createAsyncThunk(
  'mensagens/transcreverAudio',
  async (mensagemId: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.put(
        `/v2/messages/${mensagemId}/audio/transcription`,
      );
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const message =
        error?.response?.data?.message ??
        'Falha ao solicitar transcrição. Por favor, tente novamente.';
      return rejectWithValue({ message });
    }
  },
);

// reducer

const initialState = {
  replyMessage: null,
  mensagensContato: [],
  mensagensContatoCursor: { afterCursor: '', beforeCursor: '' },
  loadingMensagensContato: false,

  mensagensAtendimento: [],
  mensagensAtendimentoCursor: { afterCursor: '', beforeCursor: '' },
  loadingMensagensAtendimento: false,

  mensagensAtendimentoNaFila: [],
  mensagensAtendimentoNaFilaCursor: { afterCursor: '', beforeCursor: '' },
  loadingMensagensAtendimentoNaFila: false,

  mensagensGrupo: [],
  mensagensGrupoCursor: { afterCursor: '', beforeCursor: '' },
  loadingMensagensGrupo: false,

  mensagensFinalizadas: [],
  mensagensFinalizadasCursor: { afterCursor: '', beforeCursor: '' },
  loadingMensagensFinalizadas: false,

  respostasProntas: [],
  loadingRespostasProntas: false,

  mensagemCitada: undefined,
  loadingMensagemCitada: false,

  midiasAtendimento: [],
  documentosAtendimento: [],
  midiasGrupo: [],
  documentosGrupo: [],

  midiasAtendimentoInfoPage: {},
  documentosAtendimentoInfoPage: {},
  midiasGrupoInfoPage: {},
  documentosGrupoInfoPage: {},

  loadingMidiasAtendimento: false,
  loadingDocumentosAtendimento: false,
  loadingMidiasGrupo: false,
  loadingDocumentosGrupo: false,
  loadingEnviarArquivo: false,

  previewMensagens: [],
  mensagemIdPesquisada: null,
  searchQuery: '',
  mensagemEstaMontada: false,
  loadingPreviewMensagens: false,

  atendimentoCursorAConsumir: [],
  grupoCursorAConsumir: [],
  atendimentoFilaCursorAConsumir: [],
  historicoCursorAConsumir: [],

  reacoesMensagem: [],
  loadingReacoesMensagem: false,

  historicoMensagemEditada: [],
  loadingHistoricoMensagemEditada: false,

  mensagensParaEncaminhar: [],
  totalMensagensParaEncaminhar: 0,
  isEncaminhandoMensagens: false,
  loadingEncaminharMensagens: false,
  loadingReagirMensagem: false,
  loadingRemoverReacao: false,

  botoesInterativosDesabilitados: {},

  isGravandoAudio: false,
} as MensagensState;

export const mensagensSlice = createSlice({
  name: 'mensagens',
  initialState,
  reducers: {
    setGravandoAudio(state, action: PayloadAction<boolean>) {
      state.isGravandoAudio = action.payload;
    },
    setCursorAConsumir(
      state,
      action: PayloadAction<{
        afterCursor?: string;
        beforeCursor?: string;
        type?: 'atendimento' | 'grupo' | 'historico' | 'atendimento-na-fila';
      }>,
    ) {
      const { type = 'atendimento', ...cursor } = action.payload;

      if (type === 'atendimento') {
        state.atendimentoCursorAConsumir = [cursor];
      } else if (type === 'grupo') {
        state.grupoCursorAConsumir = [cursor];
      } else if (type === 'historico') {
        state.historicoCursorAConsumir = [cursor];
      } else if (type === 'atendimento-na-fila') {
        state.atendimentoFilaCursorAConsumir = [cursor];
      }
    },
    setBeforeCursorAConsumir(
      state,
      action: PayloadAction<{
        beforeCursor?: string;
        type?: 'atendimento' | 'grupo' | 'historico' | 'atendimento-na-fila';
      }>,
    ) {
      const { beforeCursor, type = 'atendimento' } = action.payload;

      if (type === 'atendimento') {
        state.atendimentoCursorAConsumir[0] = {
          afterCursor: state.atendimentoCursorAConsumir[0]?.afterCursor,
          beforeCursor,
        };
      } else if (type === 'grupo') {
        state.grupoCursorAConsumir[0] = {
          afterCursor: state.grupoCursorAConsumir[0]?.afterCursor,
          beforeCursor,
        };
      } else if (type === 'historico') {
        state.historicoCursorAConsumir[0] = {
          afterCursor: state.historicoCursorAConsumir[0]?.afterCursor,
          beforeCursor,
        };
      } else if (type === 'atendimento-na-fila') {
        state.atendimentoFilaCursorAConsumir[0] = {
          afterCursor: state.atendimentoFilaCursorAConsumir[0]?.afterCursor,
          beforeCursor,
        };
      }
    },
    setAfterCursorAConsumir(
      state,
      action: PayloadAction<{
        afterCursor?: string;
        type?: 'atendimento' | 'grupo' | 'historico' | 'atendimento-na-fila';
      }>,
    ) {
      const { afterCursor, type = 'atendimento' } = action.payload;

      if (type === 'atendimento') {
        state.atendimentoCursorAConsumir[0] = {
          afterCursor,
          beforeCursor: state.atendimentoCursorAConsumir[0]?.beforeCursor,
        };
      } else if (type === 'grupo') {
        state.grupoCursorAConsumir[0] = {
          afterCursor,
          beforeCursor: state.grupoCursorAConsumir[0]?.beforeCursor,
        };
      } else if (type === 'historico') {
        state.historicoCursorAConsumir[0] = {
          afterCursor,
          beforeCursor: state.historicoCursorAConsumir[0]?.beforeCursor,
        };
      } else if (type === 'atendimento-na-fila') {
        state.atendimentoFilaCursorAConsumir[0] = {
          afterCursor,
          beforeCursor: state.atendimentoFilaCursorAConsumir[0]?.beforeCursor,
        };
      }
    },
    setIdMensagemPesquisada(state, action: PayloadAction<string | null>) {
      state.mensagemIdPesquisada = action.payload;
    },
    resetIdMensagemPesquisada(state) {
      state.mensagemIdPesquisada = null;
    },
    setSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload;
    },
    resetSearchQuery(state) {
      state.searchQuery = '';
    },
    resetAtendimentoCursorAConsumir(state) {
      state.atendimentoCursorAConsumir = [];
    },
    resetGrupoCursorAConsumir(state) {
      state.grupoCursorAConsumir = [];
    },
    resetAtendimentoFilaCursorAConsumir(state) {
      state.atendimentoFilaCursorAConsumir = [];
    },
    resetHistoricoCursorAConsumir(state) {
      state.historicoCursorAConsumir = [];
    },
    setMensagemEstaMontada(state, action: PayloadAction<boolean>) {
      state.mensagemEstaMontada = action.payload;
    },
    setBotoesInterativosDesabilitados(state, action: PayloadAction<string>) {
      state.botoesInterativosDesabilitados[action.payload] = true;
    },
    setLoadingEnviarArquivo(state, action: PayloadAction<boolean>) {
      state.loadingEnviarArquivo = action.payload;
    },
    setReplyMessage(state, action: PayloadAction<MensagemInterface>) {
      state.replyMessage = action.payload;
    },
    atualizarMensagemAtendimento(
      state,
      action: PayloadAction<MensagemInterface>,
    ) {
      const messageExists = state.mensagensAtendimento.findIndex(
        ({ id }) => id === action.payload.id,
      );

      if (messageExists > -1) {
        state.mensagensAtendimento[messageExists] = action.payload;
      }
    },
    // Atualiza o status da transcrição ao receber evento do WebSocket
    atualizarTranscricaoAudio(
      state,
      action: PayloadAction<{
        atendimentoId: string;
        mensagemId: string;
        status: AudioTranscriptionStatus;
        text?: string;
      }>,
    ) {
      const { atendimentoId, mensagemId, status, text } = action.payload;
      const index = state.mensagensAtendimento.findIndex(
        m => m.id === mensagemId && m.atendimentoId === atendimentoId,
      );

      if (index > -1) {
        const msg = state.mensagensAtendimento[index];

        if (status === 'completed' && text) {
          msg.mensagem = text;
        }
        msg.messageMetadata = {
          audioTranscription: {
            status,
            statusAt: new Date().toISOString(),
            errorCode: status === 'failed' ? 'TRANSCRIPTION_FAILED' : null,
          },
        };
      }
    },

    // Atualiza o status da transcrição ao receber evento do WebSocket para grupos
    atualizarTranscricaoAudioGrupo(
      state,
      action: PayloadAction<{
        grupoChatId: string;
        mensagemId: string;
        status: AudioTranscriptionStatus;
        text?: string;
      }>,
    ) {
      const { grupoChatId, mensagemId, status, text } = action.payload;
      const index = state.mensagensGrupo.findIndex(
        m => m.id === mensagemId && m.grupoChatId === grupoChatId,
      );

      if (index > -1) {
        const msg = state.mensagensGrupo[index];

        if (status === 'completed' && text) {
          msg.mensagem = text;
        }
        msg.messageMetadata = {
          audioTranscription: {
            status,
            statusAt: new Date().toISOString(),
            errorCode: status === 'failed' ? 'TRANSCRIPTION_FAILED' : null,
          },
        };
      }
    },

    setTranscricaoSolicitada(
      state,
      action: PayloadAction<{
        atendimentoId: string;
        mensagemId: string;
        status?: AudioTranscriptionStatus;
      }>,
    ) {
      const {
        atendimentoId,
        mensagemId,
        status = 'requested',
      } = action.payload;
      const index = state.mensagensAtendimento.findIndex(
        m => m.id === mensagemId && m.atendimentoId === atendimentoId,
      );

      if (index > -1) {
        const existing =
          state.mensagensAtendimento[index].messageMetadata?.audioTranscription;

        state.mensagensAtendimento[index].messageMetadata = {
          audioTranscription: {
            status,
            statusAt:
              status === 'failed' && existing?.statusAt
                ? existing.statusAt
                : new Date().toISOString(),
            errorCode: status === 'failed' ? 'TRANSCRIPTION_TIMEOUT' : null,
          },
        };
      }
    },

    setTranscricaoSolicitadaGrupo(
      state,
      action: PayloadAction<{
        grupoChatId: string;
        mensagemId: string;
        status?: AudioTranscriptionStatus;
      }>,
    ) {
      const { grupoChatId, mensagemId, status = 'requested' } = action.payload;
      const index = state.mensagensGrupo.findIndex(
        m => m.id === mensagemId && m.grupoChatId === grupoChatId,
      );

      if (index > -1) {
        const existing =
          state.mensagensGrupo[index].messageMetadata?.audioTranscription;

        state.mensagensGrupo[index].messageMetadata = {
          audioTranscription: {
            status,
            statusAt:
              status === 'failed' && existing?.statusAt
                ? existing.statusAt
                : new Date().toISOString(),
            errorCode: status === 'failed' ? 'TRANSCRIPTION_TIMEOUT' : null,
          },
        };
      }
    },

    atualizarMensagemEditadaAtendimento(
      state,
      action: PayloadAction<AtualizarMensagemEditadaAtendimentoInterface>,
    ) {
      const messageExists = state.mensagensAtendimento.findIndex(
        ({ id }) => id === action.payload.mensagem.id,
      );

      if (messageExists > -1) {
        state.mensagensAtendimento[messageExists] = action.payload.mensagem;
      }
    },
    atualizarMensagemEditadaGrupo(
      state,
      action: PayloadAction<AtualizarMensagemEditadaGrupoInterface>,
    ) {
      const messageExists = state.mensagensGrupo.findIndex(
        ({ id }) => id === action.payload.mensagem.id,
      );

      if (messageExists > -1) {
        state.mensagensGrupo[messageExists] = action.payload.mensagem;
      }
    },
    atualizarReacoesMensagemAtendimento(
      state,
      action: PayloadAction<AtualizarReacoesMensagemInterface>,
    ) {
      const messageExists = state.mensagensAtendimento.find(
        ({ id }) => id === action.payload.reactions.msgReagidaId,
      );

      if (messageExists) {
        messageExists.reacoesEmojis = action.payload.reactions.lastReactions;
        messageExists.reacoesTotal = action.payload.reactions.total;
      }
    },
    atualizarReacoesMensagemGrupo(
      state,
      action: PayloadAction<AtualizarReacoesMensagemGrupoInterface>,
    ) {
      const messageExists = state.mensagensGrupo.find(
        ({ id }) => id === action.payload.reactions.msgReagidaId,
      );

      if (messageExists) {
        messageExists.reacoesEmojis = action.payload.reactions.lastReactions;
        messageExists.reacoesTotal = action.payload.reactions.total;
      }
    },
    addMensagemAtendimento(state, action: PayloadAction<MensagemInterface>) {
      const messageExists = state.mensagensAtendimento.find(
        ({ id }) => id === action.payload.id,
      );

      if (!messageExists) state.mensagensAtendimento.unshift(action.payload);
    },
    addMensagemGrupo(state, action: PayloadAction<MensagemInterface>) {
      const mensagem = action.payload;

      const messageExists = state.mensagensGrupo.find(
        ({ id }) => id === mensagem.id,
      );

      if (!messageExists) state.mensagensGrupo.unshift(mensagem);

      const indexMsg = state.mensagensGrupo.findIndex(
        m => m.id === mensagem.id,
      );

      // atualiza a ultima mensagem como sendo a mensagem recebida
      if (indexMsg > -1) {
        state.mensagensGrupo[indexMsg].ultimaMensagemEm =
          mensagem.ultimaMensagemEm;
      }
    },
    atualizarStatusMensagensAtendimento(
      state,
      action: PayloadAction<{
        ackStatus: StatusMensagemType;
        ackMotivoErro: string;
        ackDescricaoErro: string;
        ids: string[];
      }>,
    ) {
      // array de ids das mensagens que mudaram de status de recebimento e o ackStatus atual
      const { ids, ackStatus, ackMotivoErro, ackDescricaoErro } =
        action.payload;
      if (ids) {
        ids.forEach(mensagemId => {
          const index = state.mensagensAtendimento.findIndex(
            ({ id }) => id === mensagemId,
          );

          if (index > -1) {
            state.mensagensAtendimento[index].ackStatus = ackStatus;
            state.mensagensAtendimento[index].ackMotivoErro = ackMotivoErro;
            state.mensagensAtendimento[index].ackDescricaoErro =
              ackDescricaoErro;
          }
        });
      }
    },
    atualizarStatusMensagensGrupo(
      state,
      action: PayloadAction<{ ackStatus: StatusMensagemType; ids: string[] }>,
    ) {
      // array de ids das mensagens que mudaram de status de recebimento e o ackStatus atual
      const { ids, ackStatus } = action.payload;
      if (ids) {
        ids.forEach(mensagemId => {
          const index = state.mensagensGrupo.findIndex(
            ({ id }) => id === mensagemId,
          );

          if (index > -1) state.mensagensGrupo[index].ackStatus = ackStatus;
        });
      }
    },
    // Baixando a mídia[mensagem]  mensagens do atendimento [iniciado]
    baixandoMidiaMensagensAtendimento(
      state,
      action: PayloadAction<BaixandoMidiaMensagemInterface>,
    ) {
      const { mensagemId } = action.payload;
      state.mensagensAtendimento.forEach(mensagem => {
        if (mensagem.id === mensagemId) mensagem.baixando = true;
      });
    },
    // Atualiza a mídia[mensagem]  da lista de mensagens do atendimento [iniciado]
    atualizarMidiaMensagensAtendimento(
      state,
      action: PayloadAction<AtualizarMidiaMensagemInterface>,
    ) {
      const { mensagemId, midiaDownloadErro, midiaUrl, midiaDuracao } =
        action.payload;

      state.mensagensAtendimento.forEach(mensagem => {
        if (mensagem.id === mensagemId) {
          mensagem.midiaUrl = midiaUrl;
          mensagem.midiaDownloadErro = midiaDownloadErro;
          mensagem.baixando = false;
          if (midiaDuracao != null) {
            mensagem.midiaDuracao = midiaDuracao;
          }
        }
      });
    },
    // Baixando a mídia[mensagem]  [mensagens do contato]
    baixandoMidiaMensagensContato(
      state,
      action: PayloadAction<BaixandoMidiaMensagemInterface>,
    ) {
      const { mensagemId } = action.payload;
      state.mensagensContato.forEach(mensagem => {
        if (mensagem.id === mensagemId) mensagem.baixando = true;
      });
    },
    // Atualiza a mídia[mensagem]  do histórico de mensagens do contato
    atualizarMidiaMensagensContato(
      state,
      action: PayloadAction<AtualizarMidiaMensagemInterface>,
    ) {
      const { mensagemId, midiaDownloadErro, midiaUrl, midiaDuracao } =
        action.payload;

      state.mensagensContato.forEach(mensagem => {
        if (mensagem.id === mensagemId) {
          mensagem.midiaUrl = midiaUrl;
          mensagem.midiaDownloadErro = midiaDownloadErro;
          mensagem.baixando = false;
          if (midiaDuracao != null) {
            mensagem.midiaDuracao = midiaDuracao;
          }
        }
      });
    },
    // Baixando a mídia[mensagem] [mensagens grupo]
    baixandoMidiaMensagensGrupo(
      state,
      action: PayloadAction<BaixandoMidiaMensagemInterface>,
    ) {
      const { mensagemId } = action.payload;

      state.mensagensGrupo.forEach(mensagem => {
        if (mensagem.id === mensagemId) mensagem.baixando = true;
      });
    },
    // Atualiza mídia[mensagem] da lista de mensagens do [grupo]
    atualizarMidiaMensagensGrupo(
      state,
      action: PayloadAction<AtualizarMidiaMensagemInterface>,
    ) {
      const { mensagemId, midiaDownloadErro, midiaUrl, midiaDuracao } =
        action.payload;

      state.mensagensGrupo.forEach(mensagem => {
        if (mensagem.id === mensagemId) {
          mensagem.midiaUrl = midiaUrl;
          mensagem.midiaDownloadErro = midiaDownloadErro;
          mensagem.baixando = false;
          if (midiaDuracao != null) {
            mensagem.midiaDuracao = midiaDuracao;
          }
        }
      });
    },
    // Baixando a mídia[mensagem] [mensagens finalizadas]
    baixandoMidiaMensagensFinalizadas(
      state,
      action: PayloadAction<BaixandoMidiaMensagemInterface>,
    ) {
      const { mensagemId } = action.payload;
      state.mensagensFinalizadas.forEach(mensagem => {
        if (mensagem.id === mensagemId) mensagem.baixando = true;
      });
    },
    // Atualiza a mídia[mensagem] de um atendimento [histórico de atendimentos finalizados]
    atualizarMidiaMensagensFinalizadas(
      state,
      action: PayloadAction<AtualizarMidiaMensagemInterface>,
    ) {
      const { mensagemId, midiaDownloadErro, midiaUrl, midiaDuracao } =
        action.payload;

      state.mensagensFinalizadas.forEach(mensagem => {
        if (mensagem.id === mensagemId) {
          mensagem.midiaUrl = midiaUrl;
          mensagem.midiaDownloadErro = midiaDownloadErro;
          mensagem.baixando = false;
          if (midiaDuracao != null) {
            mensagem.midiaDuracao = midiaDuracao;
          }
        }
      });
    },
    // Baixando a mídia[mensagem]  [mídias do atendimento]
    baixandoMensagemMidiasAtendimento(
      state,
      action: PayloadAction<BaixandoMidiaMensagemInterface>,
    ) {
      const { mensagemId } = action.payload;
      state.midiasAtendimento.forEach(mensagem => {
        if (mensagem.id === mensagemId) mensagem.baixando = true;
      });
    },
    // Atualiza a mídia[mensagem] na lista de mídias do [atendimento]
    atualizarMidiasAtendimento(
      state,
      action: PayloadAction<AtualizarMidiaMensagemInterface>,
    ) {
      const { mensagemId, midiaDownloadErro, midiaUrl, midiaDuracao } =
        action.payload;

      state.midiasAtendimento.forEach(mensagem => {
        if (mensagem.id === mensagemId) {
          mensagem.midiaUrl = midiaUrl;
          mensagem.midiaDownloadErro = midiaDownloadErro;
          mensagem.baixando = false;
          if (midiaDuracao != null) {
            mensagem.midiaDuracao = midiaDuracao;
          }
        }
      });
    },
    // Baixando a mídia[mensagem] [mídias do grupo]
    baixandoMensagemMidiasGrupo(
      state,
      action: PayloadAction<BaixandoMidiaMensagemInterface>,
    ) {
      const { mensagemId } = action.payload;
      state.midiasGrupo.forEach(mensagem => {
        if (mensagem.id === mensagemId) mensagem.baixando = true;
      });
    },
    // Atualiza a mídia[mensagem] na lista de midias do [grupo]
    atualizarMidiasGrupo(
      state,
      action: PayloadAction<AtualizarMidiaMensagemInterface>,
    ) {
      const { mensagemId, midiaDownloadErro, midiaUrl, midiaDuracao } =
        action.payload;
      state.midiasGrupo.forEach(mensagem => {
        if (mensagem.id === mensagemId) {
          mensagem.midiaUrl = midiaUrl;
          mensagem.midiaDownloadErro = midiaDownloadErro;
          mensagem.baixando = false;
          if (midiaDuracao != null) {
            mensagem.midiaDuracao = midiaDuracao;
          }
        }
      });
    },
    // Baixando o documento[mensagem] [documentos do atendimento]
    baixandoMensagemDocumentosAtendimento(
      state,
      action: PayloadAction<BaixandoMidiaMensagemInterface>,
    ) {
      const { mensagemId } = action.payload;
      state.documentosAtendimento.forEach(mensagem => {
        if (mensagem.id === mensagemId) mensagem.baixando = true;
      });
    },
    // Atualiza documento[mensagem] na lista de documentos [atendimento]
    atualizarDocumentosAtendimento(
      state,
      action: PayloadAction<AtualizarMidiaMensagemInterface>,
    ) {
      const { mensagemId, midiaDownloadErro, midiaUrl, midiaDuracao } =
        action.payload;
      state.documentosAtendimento.forEach(mensagem => {
        if (mensagem.id === mensagemId) {
          mensagem.midiaUrl = midiaUrl;
          mensagem.midiaDownloadErro = midiaDownloadErro;
          mensagem.baixando = false;
          if (midiaDuracao != null) {
            mensagem.midiaDuracao = midiaDuracao;
          }
        }
      });
    },
    // Baixando o documento[mensagem] [documentos do grupo]
    baixandoMensagemDocumentosGrupo(
      state,
      action: PayloadAction<BaixandoMidiaMensagemInterface>,
    ) {
      const { mensagemId } = action.payload;
      state.documentosGrupo.forEach(mensagem => {
        if (mensagem.id === mensagemId) mensagem.baixando = true;
      });
    },
    // Atualiza o documento[mensagem] na lista de documentos [grupo]
    atualizarDocumentosGrupo(
      state,
      action: PayloadAction<AtualizarMidiaMensagemInterface>,
    ) {
      const { mensagemId, midiaDownloadErro, midiaUrl, midiaDuracao } =
        action.payload;

      state.documentosGrupo.forEach(mensagem => {
        if (mensagem.id === mensagemId) {
          mensagem.midiaUrl = midiaUrl;
          mensagem.midiaDownloadErro = midiaDownloadErro;
          mensagem.baixando = false;
          if (midiaDuracao != null) {
            mensagem.midiaDuracao = midiaDuracao;
          }
        }
      });
    },
    setMensagemApagadaAtendimento(
      state,
      action: PayloadAction<{ mensagemId: string; apagadaEm: string }>,
    ) {
      const { mensagemId, apagadaEm } = action.payload;
      const index = state.mensagensAtendimento.findIndex(
        ({ id }) => id === mensagemId,
      );

      if (index > -1) {
        state.mensagensAtendimento[index].mensagemApagadaEm = apagadaEm;
      }
    },
    setMensagemApagadaGrupo(
      state,
      action: PayloadAction<{ mensagemId: string; apagadaEm: string }>,
    ) {
      const { mensagemId, apagadaEm } = action.payload;
      const index = state.mensagensGrupo.findIndex(
        ({ id }) => id === mensagemId,
      );

      if (index > -1) {
        state.mensagensGrupo[index].mensagemApagadaEm = apagadaEm;
      }
    },
    setMensagensParaEncaminhar(
      state,
      action: PayloadAction<{
        id: string;
      }>,
    ) {
      state.isEncaminhandoMensagens = true;

      const { id } = action.payload;

      const index = state.mensagensParaEncaminhar.findIndex(
        mensagemId => mensagemId === id,
      );

      if (index === -1) {
        state.mensagensParaEncaminhar.push(id);
        state.totalMensagensParaEncaminhar += 1;
      } else {
        state.mensagensParaEncaminhar.splice(index, 1);
        state.totalMensagensParaEncaminhar -= 1;
      }
    },
    addMensagemFinalizada(state, action: PayloadAction<MensagemInterface>) {
      const messageExists = state.mensagensFinalizadas.find(
        ({ id }) => id === action.payload.id,
      );

      if (!messageExists) state.mensagensFinalizadas.unshift(action.payload);
    },
    atualizarReacoesMensagemFinalizada(
      state,
      action: PayloadAction<AtualizarReacoesMensagemInterface>,
    ) {
      const messageExists = state.mensagensFinalizadas.find(
        ({ id }) => id === action.payload.reactions.msgReagidaId,
      );

      if (messageExists) {
        messageExists.reacoesEmojis = action.payload.reactions.lastReactions;
        messageExists.reacoesTotal = action.payload.reactions.total;
      }
    },
    atualizarMensagemEditadaFinalizadas(
      state,
      action: PayloadAction<AtualizarMensagemEditadaAtendimentoInterface>,
    ) {
      const messageExists = state.mensagensFinalizadas.findIndex(
        ({ id }) => id === action.payload.mensagem.id,
      );

      if (messageExists > -1) {
        state.mensagensFinalizadas[messageExists] = action.payload.mensagem;
      }
    },
    setMensagemApagadaFinalizadas(
      state,
      action: PayloadAction<{ mensagemId: string; apagadaEm: string }>,
    ) {
      const { mensagemId, apagadaEm } = action.payload;
      const index = state.mensagensFinalizadas.findIndex(
        ({ id }) => id === mensagemId,
      );

      if (index > -1) {
        state.mensagensFinalizadas[index].mensagemApagadaEm = apagadaEm;
      }
    },
    atualizarStatusMensagensFinalizadas(
      state,
      action: PayloadAction<{
        ackStatus: StatusMensagemType;
        ackMotivoErro: string;
        ackDescricaoErro: string;
        ids: string[];
      }>,
    ) {
      // array de ids das mensagens que mudaram de status de recebimento e o ackStatus atual
      const { ids, ackStatus, ackMotivoErro, ackDescricaoErro } =
        action.payload;
      if (ids) {
        ids.forEach(mensagemId => {
          const index = state.mensagensFinalizadas.findIndex(
            ({ id }) => id === mensagemId,
          );

          if (index > -1) {
            state.mensagensFinalizadas[index].ackStatus = ackStatus;
            state.mensagensFinalizadas[index].ackMotivoErro = ackMotivoErro;
            state.mensagensFinalizadas[index].ackDescricaoErro =
              ackDescricaoErro;
          }
        });
      }
    },
    resetMensagensParaEncaminhar(state) {
      state.mensagensParaEncaminhar = [];
      state.totalMensagensParaEncaminhar = 0;
      state.isEncaminhandoMensagens = false;
    },
    resetMensagensContato(state) {
      state.mensagensContato = initialState.mensagensContato;
      state.mensagensContatoCursor = initialState.mensagensContatoCursor;
      state.loadingMensagensContato = initialState.loadingMensagensContato;
    },
    resetMensagensAtendimento(state) {
      state.mensagensAtendimento = initialState.mensagensAtendimento;
      state.mensagensAtendimentoCursor =
        initialState.mensagensAtendimentoCursor;
      state.loadingMensagensAtendimento =
        initialState.loadingMensagensAtendimento;
    },
    resetMensagensAtendimentoNaFila(state) {
      state.mensagensAtendimentoNaFila =
        initialState.mensagensAtendimentoNaFila;
      state.mensagensAtendimentoNaFilaCursor =
        initialState.mensagensAtendimentoNaFilaCursor;
      state.loadingMensagensAtendimentoNaFila =
        initialState.loadingMensagensAtendimentoNaFila;
    },
    resetMensagensGrupos(state) {
      state.mensagensGrupo = initialState.mensagensGrupo;
      state.mensagensGrupoCursor = initialState.mensagensGrupoCursor;
      state.loadingMensagensGrupo = initialState.loadingMensagensGrupo;
    },
    resetMensagensFinalizadas(state) {
      state.mensagensFinalizadas = initialState.mensagensFinalizadas;
      state.mensagensFinalizadasCursor =
        initialState.mensagensFinalizadasCursor;
      state.loadingMensagensFinalizadas =
        initialState.loadingMensagensFinalizadas;
    },
    resetRespostasProntas(state) {
      state.respostasProntas = initialState.respostasProntas;
      state.loadingRespostasProntas = initialState.loadingRespostasProntas;
    },
    resetReacoesMensagem(state) {
      state.reacoesMensagem = initialState.reacoesMensagem;
      state.loadingReacoesMensagem = initialState.loadingReacoesMensagem;
    },
    resetHistoricoMensagemEditada(state) {
      state.historicoMensagemEditada = initialState.historicoMensagemEditada;
      state.loadingHistoricoMensagemEditada =
        initialState.loadingHistoricoMensagemEditada;
    },
    resetMensagemCitada(state) {
      state.mensagemCitada = initialState.mensagemCitada;
      state.loadingMensagemCitada = initialState.loadingMensagemCitada;
    },
    resetGalerias(state) {
      state.midiasAtendimento = initialState.midiasAtendimento;
      state.documentosAtendimento = initialState.documentosAtendimento;
      state.midiasGrupo = initialState.midiasGrupo;
      state.documentosGrupo = initialState.documentosGrupo;

      state.midiasAtendimentoInfoPage = initialState.midiasAtendimentoInfoPage;
      state.documentosAtendimentoInfoPage =
        initialState.documentosAtendimentoInfoPage;
      state.midiasGrupoInfoPage = initialState.midiasGrupoInfoPage;
      state.documentosGrupoInfoPage = initialState.documentosGrupoInfoPage;
    },
    resetReplyMessage(state) {
      state.replyMessage = initialState.replyMessage;
    },
    resetPreviewMensagens(state) {
      state.previewMensagens = initialState.previewMensagens;
      state.loadingPreviewMensagens = initialState.loadingPreviewMensagens;
    },
  },
  extraReducers(builder) {
    // Fetch preview mensagens
    builder.addCase(fetchPreviewMensagens.pending, state => {
      state.loadingPreviewMensagens = true;
    });
    builder.addCase(fetchPreviewMensagens.fulfilled, (state, action) => {
      state.previewMensagens = action.payload;
      state.loadingPreviewMensagens = false;
    });
    builder.addCase(fetchPreviewMensagens.rejected, state => {
      state.loadingPreviewMensagens = false;
    });
    // Fetch mensagens por contato
    builder.addCase(fetchMensagensByContato.pending, state => {
      state.loadingMensagensContato = true;
    });
    builder.addCase(fetchMensagensByContato.fulfilled, (state, action) => {
      state.mensagensContato = state.mensagensContato.concat(
        action.payload.data,
      );
      state.mensagensContatoCursor = action.payload.cursor;
      state.loadingMensagensContato = false;
    });
    builder.addCase(fetchMensagensByContato.rejected, state => {
      state.loadingMensagensContato = false;
    });
    // Fetch mensagens por atendimento
    builder.addCase(fetchMensagensByAtendimento.pending, state => {
      state.loadingMensagensAtendimento = true;
    });
    builder.addCase(fetchMensagensByAtendimento.fulfilled, (state, action) => {
      const { data, cursor } = action.payload;
      const currentCursor = action.meta.arg.cursor;

      if (data.length === 0) {
        state.loadingMensagensAtendimento = false;
        state.mensagensAtendimentoCursor = cursor;

        return;
      }

      if (currentCursor?.beforeCursor) {
        state.mensagensAtendimento = [...data, ...state.mensagensAtendimento];
      } else {
        state.mensagensAtendimento = [...state.mensagensAtendimento, ...data];
      }

      state.mensagensAtendimentoCursor = cursor;
      state.loadingMensagensAtendimento = false;
    });
    builder.addCase(fetchMensagensByAtendimento.rejected, state => {
      state.loadingMensagensAtendimento = false;
    });
    // Fetch mensagens por grupo
    builder.addCase(fetchMensagensByGrupo.pending, state => {
      state.loadingMensagensGrupo = true;
    });
    builder.addCase(fetchMensagensByGrupo.fulfilled, (state, action) => {
      const { data, cursor } = action.payload;
      const currentCursor = action.meta.arg.cursor;

      if (data.length === 0) {
        state.loadingMensagensGrupo = false;
        state.mensagensGrupoCursor = cursor;

        return;
      }

      if (currentCursor?.beforeCursor) {
        state.mensagensGrupo = [...data, ...state.mensagensGrupo];
      } else {
        state.mensagensGrupo = [...state.mensagensGrupo, ...data];
      }

      state.mensagensGrupoCursor = cursor;
      state.loadingMensagensGrupo = false;
    });
    builder.addCase(fetchMensagensByGrupo.rejected, state => {
      state.loadingMensagensGrupo = false;
    });
    // Fetch mensagens finalizadas por atendimento
    builder.addCase(fetchMensagensFinalizadasByAtendimento.pending, state => {
      state.loadingMensagensFinalizadas = true;
    });
    builder.addCase(
      fetchMensagensFinalizadasByAtendimento.fulfilled,
      (state, action) => {
        const { data, cursor } = action.payload;
        const currentCursor = action.meta.arg.cursor;

        if (data.length === 0) {
          state.loadingMensagensFinalizadas = false;
          state.mensagensFinalizadasCursor = cursor;

          return;
        }

        if (currentCursor?.beforeCursor) {
          state.mensagensFinalizadas = [...data, ...state.mensagensFinalizadas];
        } else {
          state.mensagensFinalizadas = [...state.mensagensFinalizadas, ...data];
        }

        state.mensagensFinalizadasCursor = cursor;
        state.loadingMensagensFinalizadas = false;
      },
    );
    builder.addCase(fetchMensagensFinalizadasByAtendimento.rejected, state => {
      state.loadingMensagensFinalizadas = false;
    });

    // Fetch mensagens finalizadas por atendimento na fila
    builder.addCase(fetchMensagensFinalizadasAtendimentoFila.pending, state => {
      state.loadingMensagensAtendimentoNaFila = true;
    });
    builder.addCase(
      fetchMensagensFinalizadasAtendimentoFila.fulfilled,
      (state, action) => {
        const { data, cursor } = action.payload;
        const currentCursor = action.meta.arg.cursor;

        if (data.length === 0) {
          state.loadingMensagensAtendimentoNaFila = false;
          state.mensagensAtendimentoNaFilaCursor = cursor;

          return;
        }

        if (currentCursor?.beforeCursor) {
          state.mensagensAtendimentoNaFila = [
            ...data,
            ...state.mensagensAtendimentoNaFila,
          ];
        } else {
          state.mensagensAtendimentoNaFila = [
            ...state.mensagensAtendimentoNaFila,
            ...data,
          ];
        }

        state.mensagensAtendimentoNaFilaCursor = cursor;
        state.loadingMensagensAtendimentoNaFila = false;
      },
    );
    builder.addCase(
      fetchMensagensFinalizadasAtendimentoFila.rejected,
      state => {
        state.loadingMensagensAtendimentoNaFila = false;
      },
    );
    // Fetch respostas prontas
    builder.addCase(fetchRespostasProntas.pending, state => {
      state.loadingRespostasProntas = true;
    });
    builder.addCase(fetchRespostasProntas.fulfilled, (state, action) => {
      state.respostasProntas = action.payload;
      state.loadingRespostasProntas = false;
    });
    builder.addCase(fetchRespostasProntas.rejected, state => {
      state.loadingRespostasProntas = false;
    });
    // Fetch mensagem citada
    builder.addCase(fetchMensagemCitada.pending, state => {
      state.loadingMensagemCitada = true;
    });
    builder.addCase(fetchMensagemCitada.fulfilled, (state, action) => {
      state.mensagemCitada = action.payload;
      state.loadingMensagemCitada = false;
    });
    builder.addCase(fetchMensagemCitada.rejected, state => {
      state.loadingMensagemCitada = false;
    });
    // // Lista de imagens do [Atendimento]
    builder.addCase(fetchMidiasAtendimento.pending, state => {
      state.loadingMidiasAtendimento = true;
    });
    builder.addCase(fetchMidiasAtendimento.fulfilled, (state, action) => {
      state.midiasAtendimento = state.midiasAtendimento.concat(
        action.payload.data,
      );
      state.midiasAtendimentoInfoPage = action.payload.pageInfo;
      state.loadingMidiasAtendimento = false;
    });
    builder.addCase(fetchMidiasAtendimento.rejected, state => {
      state.loadingMidiasAtendimento = false;
    });
    // Lista de documentos do [Atendimento]
    builder.addCase(fetchDocumentosAtendimento.pending, state => {
      state.loadingDocumentosAtendimento = true;
    });
    builder.addCase(fetchDocumentosAtendimento.fulfilled, (state, action) => {
      state.documentosAtendimento = state.documentosAtendimento.concat(
        action.payload.data,
      );
      state.documentosAtendimentoInfoPage = action.payload.pageInfo;
      state.loadingDocumentosAtendimento = false;
    });
    builder.addCase(fetchDocumentosAtendimento.rejected, state => {
      state.loadingDocumentosAtendimento = false;
    });
    // Lista de imagens do [Grupo]
    builder.addCase(fetchMidiasGrupo.pending, state => {
      state.loadingMidiasGrupo = true;
    });
    builder.addCase(fetchMidiasGrupo.fulfilled, (state, action) => {
      state.midiasGrupo = state.midiasGrupo.concat(action.payload.data);
      state.midiasGrupoInfoPage = action.payload.pageInfo;
      state.loadingMidiasGrupo = false;
    });
    builder.addCase(fetchMidiasGrupo.rejected, state => {
      state.loadingMidiasGrupo = false;
    });
    // Lista de documentos do [Grupo]
    builder.addCase(fetchDocumentosGrupo.pending, state => {
      state.loadingDocumentosGrupo = true;
    });
    builder.addCase(fetchDocumentosGrupo.fulfilled, (state, action) => {
      state.documentosGrupo = state.documentosGrupo.concat(action.payload.data);
      state.documentosGrupoInfoPage = action.payload.pageInfo;
      state.loadingDocumentosGrupo = false;
    });
    builder.addCase(fetchDocumentosGrupo.rejected, state => {
      state.loadingDocumentosGrupo = false;
    });
    // Reacoes da mensagem
    builder.addCase(fetchReacoesMensagem.pending, state => {
      state.loadingReacoesMensagem = true;
    });
    builder.addCase(fetchReacoesMensagem.fulfilled, (state, action) => {
      state.reacoesMensagem = action.payload;
      state.loadingReacoesMensagem = false;
    });
    builder.addCase(fetchReacoesMensagem.rejected, state => {
      state.loadingReacoesMensagem = false;
    });

    // Histórico da mensagem editada
    builder.addCase(fetchHistoricoMensagemEditada.pending, state => {
      state.loadingHistoricoMensagemEditada = true;
    });
    builder.addCase(
      fetchHistoricoMensagemEditada.fulfilled,
      (state, action) => {
        state.historicoMensagemEditada = action.payload;
        state.loadingHistoricoMensagemEditada = false;
      },
    );
    builder.addCase(fetchHistoricoMensagemEditada.rejected, state => {
      state.loadingHistoricoMensagemEditada = false;
    });
    builder.addCase(encaminharMensagens.pending, state => {
      state.loadingEncaminharMensagens = true;
    });
    builder.addCase(encaminharMensagens.fulfilled, state => {
      state.loadingEncaminharMensagens = false;
    });
    builder.addCase(encaminharMensagens.rejected, state => {
      state.loadingEncaminharMensagens = false;
    });
    builder.addCase(reagirMensagem.pending, state => {
      state.loadingReagirMensagem = true;
    });
    builder.addCase(reagirMensagem.fulfilled, state => {
      state.loadingReagirMensagem = false;
    });
    builder.addCase(reagirMensagem.rejected, state => {
      state.loadingReagirMensagem = false;
    });
    builder.addCase(removerReacao.pending, state => {
      state.loadingRemoverReacao = true;
    });
    builder.addCase(removerReacao.fulfilled, state => {
      state.loadingRemoverReacao = false;
    });
    builder.addCase(removerReacao.rejected, state => {
      state.loadingRemoverReacao = false;
    });
  },
});

// actions

export const {
  setGravandoAudio,
  setCursorAConsumir,
  setBeforeCursorAConsumir,
  setAfterCursorAConsumir,
  setIdMensagemPesquisada,
  setMensagemEstaMontada,
  setBotoesInterativosDesabilitados,
  setLoadingEnviarArquivo,
  setReplyMessage,
  resetReplyMessage,
  addMensagemAtendimento,
  addMensagemGrupo,
  setMensagemApagadaAtendimento,
  setMensagemApagadaGrupo,
  setMensagensParaEncaminhar,
  atualizarStatusMensagensAtendimento,
  atualizarStatusMensagensGrupo,
  // Atualizar mensagem [atendimento iniciado]
  baixandoMidiaMensagensAtendimento,
  atualizarMidiaMensagensAtendimento,
  // Atualizar mensagem [mensagens do grupo]
  baixandoMidiaMensagensGrupo,
  atualizarMidiaMensagensGrupo,
  // Atualizar mensagem [mensagens do contato]
  baixandoMidiaMensagensContato,
  atualizarMidiaMensagensContato,
  // Atualizar mensagem [midias do atendimento]
  baixandoMensagemMidiasAtendimento,
  atualizarMidiasAtendimento,
  // Atualizar mensagem [mensagens finalizadas]
  baixandoMidiaMensagensFinalizadas,
  atualizarMidiaMensagensFinalizadas,
  // Atualizar mensagem [midias do grupo]
  baixandoMensagemMidiasGrupo,
  atualizarMidiasGrupo,
  // Atualizar mensagem [documentos do atendimento]
  baixandoMensagemDocumentosAtendimento,
  atualizarDocumentosAtendimento,
  // Atualizar mensagem [documentos do grupo]
  baixandoMensagemDocumentosGrupo,
  // Atualizar as reações da mensagem [atendimento]
  atualizarReacoesMensagemAtendimento,
  // Atualizar as reações da mensagem [grupo]
  atualizarReacoesMensagemGrupo,
  atualizarDocumentosGrupo,
  resetMensagensContato,
  resetMensagensAtendimento,
  resetMensagensAtendimentoNaFila,
  resetMensagensGrupos,
  resetMensagensFinalizadas,
  resetRespostasProntas,
  resetMensagemCitada,
  resetGalerias,
  resetIdMensagemPesquisada,
  setSearchQuery,
  resetSearchQuery,
  resetAtendimentoCursorAConsumir,
  resetGrupoCursorAConsumir,
  resetHistoricoCursorAConsumir,
  resetAtendimentoFilaCursorAConsumir,
  resetMensagensParaEncaminhar,
  atualizarMensagemEditadaAtendimento,
  atualizarMensagemEditadaGrupo,
  resetHistoricoMensagemEditada,
  resetReacoesMensagem,
  atualizarMensagemAtendimento,
  atualizarTranscricaoAudio,
  atualizarTranscricaoAudioGrupo,
  setTranscricaoSolicitada,
  setTranscricaoSolicitadaGrupo,
  resetPreviewMensagens,
  addMensagemFinalizada,
  atualizarReacoesMensagemFinalizada,
  atualizarMensagemEditadaFinalizadas,
  setMensagemApagadaFinalizadas,
  atualizarStatusMensagensFinalizadas,
} = mensagensSlice.actions;

// selectors

export const mensagensSelector = (state: RootState) => state.mensagens;

export default mensagensSlice.reducer;
